import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';
import { API } from '@/App';

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking');
  const [transaction, setTransaction] = useState(null);

  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/subscription/checkout/status/${sessionId}`, {
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to check payment status');
      }

      const data = await response.json();
      setTransaction(data);

      if (data.payment_status === 'paid') {
        setStatus('success');
        return;
      } else if (data.status === 'expired') {
        setStatus('expired');
        return;
      }

      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      setStatus('error');
      return;
    }

    pollPaymentStatus(sessionId);
  }, [searchParams, pollPaymentStatus]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="max-w-md w-full">
        {status === 'checking' && (
          <div className="text-center p-12 rounded-3xl bg-white border border-slate-200 shadow-xl" data-testid="payment-checking-state">
            <Loader2 className="w-16 h-16 text-violet-600 animate-spin mx-auto mb-6" />
            <h2 className="text-2xl font-bold mb-2">Processing Payment</h2>
            <p className="text-slate-600">Please wait while we confirm your payment...</p>
          </div>
        )}

        {status === 'success' && (
          <div className="text-center p-12 rounded-3xl bg-white border border-slate-200 shadow-xl" data-testid="payment-success-state">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h2 className="text-3xl font-bold mb-3">Payment Successful!</h2>
            <p className="text-slate-600 mb-2">Thank you for your subscription</p>
            {transaction && (
              <div className="mt-6 p-4 rounded-xl bg-slate-50 text-left">
                <div className="flex justify-between mb-2">
                  <span className="text-slate-600">Plan:</span>
                  <span className="font-bold capitalize">{transaction.plan_id}</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-slate-600">Amount:</span>
                  <span className="font-bold">${transaction.amount}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Billing:</span>
                  <span className="font-bold capitalize">{transaction.billing_cycle}</span>
                </div>
              </div>
            )}
            <Button
              onClick={() => navigate('/dashboard')}
              className="w-full mt-8 h-12 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white font-bold"
              data-testid="go-to-dashboard-btn"
            >
              Go to Dashboard
            </Button>
          </div>
        )}

        {(status === 'error' || status === 'expired' || status === 'timeout') && (
          <div className="text-center p-12 rounded-3xl bg-white border border-slate-200 shadow-xl" data-testid="payment-error-state">
            <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-12 h-12 text-red-600" />
            </div>
            <h2 className="text-3xl font-bold mb-3">
              {status === 'timeout' ? 'Payment Check Timeout' : 'Payment Failed'}
            </h2>
            <p className="text-slate-600 mb-8">
              {status === 'timeout'
                ? 'We are still processing your payment. Please check your email for confirmation.'
                : 'There was an issue processing your payment. Please try again.'}
            </p>
            <div className="flex flex-col gap-3">
              <Button
                onClick={() => navigate('/pricing')}
                className="w-full h-12 rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white font-bold"
                data-testid="try-again-btn"
              >
                Try Again
              </Button>
              <Button
                onClick={() => navigate('/dashboard')}
                variant="outline"
                className="w-full h-12 rounded-xl border-2 border-slate-200"
                data-testid="back-to-dashboard-btn"
              >
                Back to Dashboard
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;